import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { OwnerMyClubLink } from '#src/components/TokenPreview/TokenOwner/index.tsx';
import { PriceHistoryDate } from '#src/components/price/PriceHistoryDate/index.tsx';
import { TransactionLabel } from '#src/components/price/TransactionLabel/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import type { TransactionType } from '#src/lib/transactions.ts';
import { getTransactionType } from '#src/lib/transactions.ts';

import { DumbGraph } from './DumbGraph/index.tsx';
import type { PriceGraph_tokenPrice } from './__generated__/index.graphql.ts';

const CardWrapper = styled.div`
  width: 40px;
`;

const SmallGraphSkeleton = styled(Skeleton)`
  width: 100%;
  aspect-ratio: 313/108;
  border-radius: var(--unit);
`;

type Props = {
  small?: boolean;
  currency?: Currency;
  transactionTypes?: TransactionType[];
  loading: boolean;
  tokenPrices: PriceGraph_tokenPrice[];
  withLegend?: boolean;
  from: Date;
  to: Date;
};

export const PriceGraph = ({
  small,
  loading,
  tokenPrices,
  currency: currencyProps,
  transactionTypes,
  withLegend,
  from,
  to,
}: Props) => {
  const { sport } = useSportContext();
  const { isMe } = useCurrentUserContext();
  const { currency: userCurrency, cryptocurrency } = useCurrentUserCurrencies();

  const currency = currencyProps || userCurrency;
  const {
    toMonetaryAmount,
    getUserFiatAmountInMajorUnit,
    getUserCryptoAmountInMajorUnit,
  } = useMonetaryAmount();

  const getAmount = (monetaryAmount: MonetaryAmount) => {
    if (currency === Currency.ETH) {
      return getUserCryptoAmountInMajorUnit(monetaryAmount);
    }
    return getUserFiatAmountInMajorUnit(monetaryAmount);
  };

  const dataPoints =
    tokenPrices
      .map(tokenPrice => {
        const fullMonetaryAmount = toMonetaryAmount(tokenPrice.amounts);
        const { deal, card } = tokenPrice;

        const { seller, buyer } = deal;
        return {
          x: tokenPrice.date.toISOString(),
          y: getAmount(fullMonetaryAmount),
          type: getTransactionType({ deal: tokenPrice.deal }),
          tooltipContent: (
            <Horizontal gap={4}>
              <Horizontal>
                {!!card && (
                  <CardWrapper>
                    <FlexCard card={card} width={40} withLink />
                  </CardWrapper>
                )}
                <Vertical gap={0.5}>
                  <LabelL bold>
                    <TransactionLabel deal={tokenPrice.deal} />
                  </LabelL>
                  <LabelM color="var(--c-nd-700)">
                    <PriceHistoryDate date={tokenPrice.date} />
                  </LabelM>
                  <Horizontal gap={0.5}>
                    {(!!buyer && isType(buyer, 'User') && (
                      <OwnerMyClubLink user={buyer} sport={sport} withAvatar />
                    )) ||
                      '—'}
                  </Horizontal>
                </Vertical>
              </Horizontal>
              <Vertical className="text-right">
                <AmountWithConversion
                  column
                  monetaryAmount={tokenPrice.amounts}
                />
              </Vertical>
            </Horizontal>
          ),
          isSeller: isMe(seller?.slug),
          isBuyer: isMe(buyer?.slug),
        };
      })
      .reverse() || [];

  if (small) {
    if (loading)
      return (
        <Horizontal>
          <SmallGraphSkeleton />
        </Horizontal>
      );
  }

  return (
    <DumbGraph
      cryptocurrency={cryptocurrency}
      from={from}
      to={to}
      data={dataPoints}
      loading={loading}
      currency={currency}
      small={!!small}
      withLegend={!!withLegend}
      transactionTypes={transactionTypes}
    />
  );
};

PriceGraph.fragments = {
  tokenPrice: gql`
    fragment PriceGraph_tokenPrice on TokenPrice {
      id
      amounts {
        ...useMonetaryAmount_monetaryAmount
      }
      date
      card {
        slug
        ...FlexCard_anyCard
      }
      deal {
        ... on TokenOperationTradeInterface {
          id
          buyer: userBuyer {
            slug
            ...OwnerMyClubLink_User
          }
          seller: userSeller {
            slug
          }
        }
        ...getTransactionType_tokenDeal
      }
    }
    ${OwnerMyClubLink.fragments.user}
    ${FlexCard.fragments.anyCard}
    ${getTransactionType.fragments.deal}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<PriceGraph_tokenPrice>,
};
